import { useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { LogOut, Bot, MessageCircleQuestion } from 'lucide-react';
import { cn } from '@/lib/utils';
import { navGroups } from './nav-config';
import { ExtraNavBadges, extraBadgeLabel } from './ExtraNavBadges';
import { useAuth } from '../../hooks/useAuth';
import { useBillingFeatures } from '../../hooks/useBilling';
import { useIsProductionEnvironment } from '../../hooks/useSystemEnvironment';
import { useDomainEvents } from '../../hooks/useDomainEvents';
import {
  useAgentReviewQueuePendingCount,
  AGENT_REVIEW_QUEUE_PENDING_COUNT_KEY,
} from '../../hooks/useAgentReviewQueueCount';
import { InstallAppMenuItem } from '../InstallAppMenuItem';

interface SidebarProps {
  badges?: Record<string, number>;
}

export function Sidebar({ badges = {} }: SidebarProps) {
  const { manager, logout } = useAuth();
  const navigate = useNavigate();
  const { data: featuresData } = useBillingFeatures();
  const isProduction = useIsProductionEnvironment();
  const hasAgenticSupport =
    featuresData !== undefined && featuresData.features.includes('agentic_support');

  // Pending-holds badge — server-derived (BR-15). Only fetched when agentic_support is active
  // (avoids 401/403 for unpermissioned managers).
  const { data: agentHoldCount = 0 } = useAgentReviewQueuePendingCount(hasAgenticSupport);

  // BR-7 — reactive on agent.draft_held from ANY producer (both hold sites + the reminder
  // job), not just holds resolved on this tab's queue page. Replaces the legacy
  // 'agent-queue-count-changed' window CustomEvent.
  const queryClient = useQueryClient();
  useDomainEvents({
    'agent.draft_held': useCallback(() => {
      void queryClient.invalidateQueries({ queryKey: AGENT_REVIEW_QUEUE_PENDING_COUNT_KEY });
    }, [queryClient]),
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <aside
      className="hidden md:flex fixed top-0 bottom-[var(--cookie-consent-height,0px)] left-0 z-40 w-[var(--sidebar-width)] flex-col border-r border-border bg-background"
      aria-label="Sidebar"
    >
      {/* Brand */}
      <div className="flex h-14 items-center px-5 border-b border-border shrink-0">
        <h1 className="text-sm font-semibold text-foreground tracking-tight">
          <span className="hidden lg:inline">Landlord Portal</span>
          <span className="lg:hidden" aria-hidden="true">LP</span>
        </h1>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-3" aria-label="Main navigation">
        {navGroups.map((group) => (
          <div key={group.label} className="mb-4">
            {/* AC-L8: at the 768-1023px icon-only rail tier, the sidebar is 4rem wide —
                too narrow for group labels; hide them there (CSS-only, `hidden lg:inline`
                matches the same 1024px cutover as the --sidebar-width token override). */}
            <h3 className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hidden lg:block">
              {group.label}
            </h3>
            {group.items.filter(
              (item) =>
                (!item.featureKey ||
                  !featuresData ||
                  featuresData.features.includes(item.featureKey)) &&
                !(item.hiddenInProduction && isProduction),
            ).map((item) => {
              const Icon = item.icon;
              // BR-38 — generic parent/child badge hierarchy: when an item declares
              // childBadgeKeys, prefer a precomputed badges[item.badgeKey] value (e.g.
              // AppShell's non-double-counting leasingTotalCount) and fall back to summing
              // the child keys itself. Not leasing-specific — any NavItem can opt in.
              const badge = item.badgeKey
                ? (badges[item.badgeKey] ??
                    (item.childBadgeKeys
                      ? item.childBadgeKeys.reduce((sum, key) => sum + (badges[key] ?? 0), 0)
                      : undefined))
                : undefined;

              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  aria-label={item.label + (badge != null && badge > 0 ? `, ${badge} ${item.badgeUnit ?? 'unread'}` : '') + extraBadgeLabel(item, badges)}
                  title={item.label}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors min-h-[44px] min-w-[44px] justify-center lg:min-h-0 lg:min-w-0 lg:justify-start',
                      isActive
                        ? 'bg-accent text-accent-foreground font-medium'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                    )
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1 truncate hidden lg:inline">{item.label}</span>
                  {badge != null && badge > 0 && (
                    <span
                      className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground"
                      data-testid={item.badgeKey ? `${item.label.toLowerCase().replace(/\s+/g, '-')}-nav-badge` : undefined}
                      aria-hidden="true"
                    >
                      {badge}
                    </span>
                  )}
                  <ExtraNavBadges item={item} badges={badges} testIdPrefix={item.label.toLowerCase().replace(/\s+/g, '-')} />
                </NavLink>
              );
            })}
          </div>
        ))}

        {/* Agent review queue — shown only when agentic_support is active (BR-QUEUE-AUTHZ) */}
        {hasAgenticSupport && (
          <div className="mb-4">
            <h3 className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hidden lg:block">
              Agent
            </h3>
            <NavLink
              to="/agent/review-queue"
              data-testid="nav-agent-review-queue"
              aria-label={`Review Queue${agentHoldCount > 0 ? `, ${agentHoldCount} pending agent draft${agentHoldCount !== 1 ? 's' : ''}` : ''}`}
              title="Review Queue"
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors min-h-[44px] min-w-[44px] justify-center lg:min-h-0 lg:min-w-0 lg:justify-start',
                  isActive
                    ? 'bg-accent text-accent-foreground font-medium'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )
              }
            >
              <Bot className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="flex-1 truncate hidden lg:inline">Review Queue</span>
              {agentHoldCount > 0 && (
                <span
                  className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground"
                  data-testid="agent-review-queue-nav-badge"
                  aria-hidden="true"
                >
                  {agentHoldCount}
                </span>
              )}
            </NavLink>
            <NavLink
              to="/agent/escalations"
              data-testid="nav-agent-escalations"
              aria-label="Escalations"
              title="Escalations"
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors min-h-[44px] min-w-[44px] justify-center lg:min-h-0 lg:min-w-0 lg:justify-start',
                  isActive
                    ? 'bg-accent text-accent-foreground font-medium'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )
              }
            >
              <MessageCircleQuestion className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="flex-1 truncate hidden lg:inline">Escalations</span>
            </NavLink>
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="border-t border-border p-3 shrink-0">
        <div className="px-2 mb-2 text-xs text-muted-foreground truncate">
          {manager?.email}
        </div>
        <InstallAppMenuItem />
        <button
          type="button"
          onClick={handleLogout}
          className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          <LogOut className="h-4 w-4 shrink-0" />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
}
